import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconSheets() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ── Feature fields ────────────────────────────────────────────────────────────
// optional: true → not required for import; blank cells will be auto-generated

const FEATURE_FIELDS = [
  { key: 'feature_id',    label: 'Feature ID',    optional: true  },
  { key: 'major_feature', label: 'Major Feature', optional: false },
  { key: 'sub_feature',   label: 'Sub Feature',   optional: false },
  { key: 'description',   label: 'Description',   optional: false },
];

const REQUIRED_FIELDS = FEATURE_FIELDS.filter(f => !f.optional);

// ── Code-paths modes ──────────────────────────────────────────────────────────

const CODE_PATH_MODES = [
  {
    value: 'separate',
    label: 'Separate columns',
    description: 'Repo name is in one column; glob pattern(s) in another. Multiple globs can be separated by newlines or a pipe (|) in a single cell.',
  },
  {
    value: 'path_prefix',
    label: 'Path format  (repo/glob)',
    description: 'A single column contains entries like telephony-service/src/**/*.java — the first path segment is the repo name. Multiple entries can be separated by newlines or a pipe (|).',
  },
  {
    value: 'shared_repo',
    label: 'Shared repo name',
    description: 'All features share the same repo. Enter the repo name once and pick the column that holds the glob pattern(s). Multiple globs can be separated by newlines or a pipe (|).',
  },
];

const EMPTY_CODE_PATHS = { mode: 'separate', repoCol: '', fileCol: '', repoFixed: '' };

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  const steps = [
    { n: 1, label: 'Connect' },
    { n: 2, label: 'Map Columns' },
    { n: 3, label: 'Done' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
              background: current === s.n
                ? 'var(--accent)'
                : current > s.n ? 'var(--success, #22c55e)' : 'var(--surface-2, #1e293b)',
              color: current >= s.n ? '#fff' : 'var(--text-muted)',
              border: current === s.n ? '2px solid var(--accent)' : '2px solid transparent',
              flexShrink: 0,
            }}>
              {current > s.n ? <IconCheck /> : s.n}
            </div>
            <span style={{
              fontSize: 13,
              fontWeight: current === s.n ? 600 : 400,
              color: current === s.n ? 'var(--text-primary)' : 'var(--text-muted)',
            }}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, minWidth: 24, maxWidth: 60, margin: '0 10px',
              background: current > s.n ? 'var(--success, #22c55e)' : 'var(--border)',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Shared select helper ──────────────────────────────────────────────────────

function ColSelect({ value, onChange, headers, placeholder = '— select column —' }) {
  return (
    <select
      className="form-input"
      style={{ margin: 0, maxWidth: 260, fontSize: 13 }}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {headers.map(h => <option key={h} value={h}>{h}</option>)}
    </select>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export default function SheetImportWizard() {
  const navigate = useNavigate();

  const [step, setStep]   = useState(1);
  const [error, setError] = useState('');

  // Step 1
  const [sheetUrl, setSheetUrl]       = useState('');
  const [productId, setProductId]     = useState('');
  const [products, setProducts]       = useState([]);
  const [accessToken, setAccessToken] = useState('');
  const [analyzing, setAnalyzing]     = useState(false);

  // Step 2
  const [sheetMeta, setSheetMeta]         = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [mapping, setMapping]             = useState({});       // { major_feature: 'Col', ... }
  const [codePathsCfg, setCodePathsCfg]   = useState(EMPTY_CODE_PATHS);
  const [importing, setImporting]         = useState(false);

  // Step 3
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    axios.get('/products', { params: { limit: 200 } })
      .then(res => setProducts(res.data.data || []))
      .catch(() => {});
  }, []);

  // Reset field mapping when sheet changes
  useEffect(() => {
    setMapping({});
    setCodePathsCfg(EMPTY_CODE_PATHS);
  }, [selectedSheet]);

  const currentHeaders  = sheetMeta.find(s => s.title === selectedSheet)?.headers  ?? [];
  const currentRowCount = sheetMeta.find(s => s.title === selectedSheet)?.row_count ?? 0;

  // ── Step 1: Google OAuth → preview ─────────────────────────────────────

  const runAnalyze = useCallback(async (token) => {
    setError('');
    setAnalyzing(true);
    try {
      const res = await axios.post('/import/sheets/preview', {
        spreadsheet_url: sheetUrl.trim(),
        access_token:    token,
      });
      const sheets = res.data.sheets || [];
      setSheetMeta(sheets);
      setSelectedSheet(sheets[0]?.title || '');
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to read spreadsheet. Check the URL and try again.');
    } finally {
      setAnalyzing(false);
    }
  }, [sheetUrl]);

  const googleLogin = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/spreadsheets',
    onSuccess: tokenResponse => {
      const token = tokenResponse.access_token;
      setAccessToken(token);
      runAnalyze(token);
    },
    onError: () => {
      setError('Google sign-in failed or was cancelled.');
      setAnalyzing(false);
    },
  });

  function handleAnalyze() {
    setError('');
    if (!sheetUrl.trim()) { setError('Please enter a Google Sheets URL.'); return; }
    if (!productId)       { setError('Please select a product.'); return; }
    if (!sheetUrl.includes('docs.google.com/spreadsheets')) {
      setError('The URL does not look like a Google Sheets link.'); return;
    }
    setAnalyzing(true);
    googleLogin();
  }

  // ── Step 2: validate + execute import ──────────────────────────────────

  function validateStep2() {
    // Only required fields must be mapped; optional fields (feature_id) may be left blank
    for (const f of REQUIRED_FIELDS) {
      if (!mapping[f.key]) {
        return `"${f.label}" column mapping is required.`;
      }
    }
    // Code paths config — always required
    const { mode, repoCol, fileCol, repoFixed } = codePathsCfg;
    if (!mode) return 'Please select a code path mode.';
    if (mode === 'separate') {
      if (!repoCol) return 'Separate columns: please select the Repo column.';
      if (!fileCol) return 'Separate columns: please select the Code File column.';
    }
    if (mode === 'path_prefix') {
      if (!fileCol) return 'Path format: please select the Code Paths column.';
    }
    if (mode === 'shared_repo') {
      if (!repoFixed.trim()) return 'Shared repo: please enter the repository name.';
      if (!fileCol) return 'Shared repo: please select the Code File column.';
    }
    return null;
  }

  // onDuplicate: 'abort' (default) | 'replace' | 'skip' — see the duplicates screen
  async function handleImport(onDuplicate = 'abort') {
    setError('');
    const validationError = validateStep2();
    if (validationError) { setError(validationError); return; }

    setImporting(true);
    try {
      const res = await axios.post('/import/sheets/execute', {
        spreadsheet_url:   sheetUrl.trim(),
        access_token:      accessToken,
        product_id:        Number(productId),
        sheet_title:       selectedSheet,
        column_mapping:    mapping,
        on_duplicate:      onDuplicate,
        code_paths_config: {
          mode:       codePathsCfg.mode,
          repo_col:   codePathsCfg.repoCol   || null,
          file_col:   codePathsCfg.fileCol   || null,
          repo_fixed: codePathsCfg.repoFixed || null,
        },
      });
      setImportResult(res.data);
      setStep(3);
    } catch (e) {
      const data = e.response?.data || {};
      if (data.status === 'unresolved_repos') {
        setImportResult(data);
        setStep(3);
      } else {
        setError(data.error || 'Import failed. Please try again.');
      }
    } finally {
      setImporting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="page-header">
        <h2>Import Features from Google Sheets</h2>
        <p>Connect a spreadsheet, map its columns to feature fields, and import in bulk</p>
      </div>

      <div className="page-body" style={{ maxWidth: 900 }}>
        <button
          className="btn btn-ghost"
          style={{ marginBottom: 20, paddingLeft: 0 }}
          onClick={() => navigate('/features')}
        >
          <IconArrowLeft /> Back to Features
        </button>

        <div className="card">
          <StepIndicator current={step} />

          {error && (
            <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>
          )}

          {step === 1 && (
            <Step1
              sheetUrl={sheetUrl} setSheetUrl={setSheetUrl}
              productId={productId} setProductId={setProductId}
              products={products}
              analyzing={analyzing} onAnalyze={handleAnalyze}
            />
          )}

          {step === 2 && (
            <Step2
              sheetMeta={sheetMeta}
              selectedSheet={selectedSheet} setSelectedSheet={setSelectedSheet}
              headers={currentHeaders} rowCount={currentRowCount}
              mapping={mapping} setMapping={setMapping}
              codePathsCfg={codePathsCfg} setCodePathsCfg={setCodePathsCfg}
              importing={importing}
              onBack={() => { setStep(1); setError(''); }}
              onImport={() => handleImport('abort')}
            />
          )}

          {step === 3 && importResult && (
            <Step3
              result={importResult}
              importing={importing}
              onBackToMapping={() => { setStep(2); setImportResult(null); setError(''); }}
              onCancel={() => navigate('/features')}
              onViewFeatures={() => navigate(`/features?product_id=${importResult.product_id}`)}
              onResolveDuplicates={mode => handleImport(mode)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 1 ────────────────────────────────────────────────────────────────────

function Step1({ sheetUrl, setSheetUrl, productId, setProductId, products, analyzing, onAnalyze }) {
  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: 15 }}>Step 1 — Spreadsheet &amp; Product</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
        Paste the Google Sheets URL and choose the product these features belong to.
        You will be asked to grant read/write access to your spreadsheet so unresolved
        code-path remarks can be written back to the sheet.
      </p>

      <div className="form-group">
        <label className="form-label" htmlFor="sheet-url">
          Google Sheets URL <span style={{ color: 'var(--danger)' }}>*</span>
        </label>
        <input
          id="sheet-url"
          className="form-input"
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={sheetUrl}
          onChange={e => setSheetUrl(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          The spreadsheet must be accessible to the Google account you sign in with.
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: 28 }}>
        <label className="form-label" htmlFor="product-select">
          Product <span style={{ color: 'var(--danger)' }}>*</span>
        </label>
        <select
          id="product-select"
          className="form-input"
          value={productId}
          onChange={e => setProductId(e.target.value)}
          style={{ maxWidth: 320 }}
        >
          <option value="">— Select a product —</option>
          {products.map(p => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          All imported features will be assigned to this product.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          className="btn btn-primary"
          onClick={onAnalyze}
          disabled={analyzing}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {analyzing
            ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Connecting…</>
            : <><IconSheets /> Connect Google &amp; Analyze</>}
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          A Google sign-in popup will appear to authorize spreadsheet access.
        </span>
      </div>
    </div>
  );
}

// ── Step 2 ────────────────────────────────────────────────────────────────────

function Step2({
  sheetMeta, selectedSheet, setSelectedSheet,
  headers, rowCount,
  mapping, setMapping,
  codePathsCfg, setCodePathsCfg,
  importing, onBack, onImport,
}) {
  const canImport = REQUIRED_FIELDS.every(f => mapping[f.key]) && headers.length > 0;

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: 15 }}>Step 2 — Select Sheet &amp; Map Columns</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
        Choose the sheet tab, map every feature field to a column, then configure how code paths are stored.
        Ensure the selected sheet already contains a header column named <strong>Import Remarks</strong>
        (exact text) for unresolved-repository notes.
      </p>

      {/* Sheet tab selector */}
      <div className="form-group" style={{ marginBottom: 24 }}>
        <label className="form-label">Sheet Tab</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select
            className="form-input"
            style={{ maxWidth: 280 }}
            value={selectedSheet}
            onChange={e => setSelectedSheet(e.target.value)}
          >
            {sheetMeta.map(s => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
          </select>
          {selectedSheet && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {rowCount.toLocaleString()} data row{rowCount !== 1 ? 's' : ''} detected
            </span>
          )}
        </div>
      </div>

      {headers.length > 0 ? (
        <>
          {/* ── Feature field mapping ── */}
          <SectionLabel>Feature Fields</SectionLabel>
          <div style={{
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            overflow: 'hidden', marginBottom: 6,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2, #1e293b)' }}>
                  <th style={thStyle}>Feature Field</th>
                  <th style={thStyle}>Spreadsheet Column</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_FIELDS.map((field, i) => (
                  <tr key={field.key} style={{
                    borderBottom: i < FEATURE_FIELDS.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <td style={tdLabelStyle}>
                      {field.label}{' '}
                      {field.optional
                        ? <span style={{ fontSize: 10, color: 'var(--text-muted)',
                            background: 'rgba(255,255,255,0.06)', borderRadius: 4,
                            padding: '1px 5px', fontWeight: 600, letterSpacing: '0.04em' }}>
                            optional
                          </span>
                        : <span style={{ color: 'var(--danger)' }}>*</span>
                      }
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <ColSelect
                        value={mapping[field.key] || ''}
                        onChange={val => setMapping(m => ({ ...m, [field.key]: val || undefined }))}
                        headers={headers}
                        placeholder={field.optional ? '— skip (auto-generate) —' : '— select column —'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 24 }}>
            Major Feature, Sub Feature, and Description are required — rows with a blank value in any
            of these columns will be skipped. Feature ID is optional: if mapped and the cell is
            non-blank the value from the sheet is used; otherwise an ID is auto-generated.
          </div>

          {/* ── Code paths configuration ── */}
          <CodePathsSection
            headers={headers}
            cfg={codePathsCfg}
            setCfg={setCodePathsCfg}
          />
        </>
      ) : (
        <div style={{
          padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
          border: '1px dashed var(--border)', borderRadius: 'var(--radius)', marginBottom: 28,
        }}>
          No column headers detected in the first row of this sheet.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onBack} disabled={importing}>
          <IconArrowLeft /> Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onImport}
          disabled={importing || !canImport}
          title={!canImport ? 'Map all required feature fields first' : undefined}
        >
          {importing
            ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Importing…</>
            : 'Run Import'}
        </button>
      </div>
    </div>
  );
}

// ── Code paths section ────────────────────────────────────────────────────────

function CodePathsSection({ headers, cfg, setCfg }) {
  function set(key, val) {
    setCfg(c => ({ ...c, [key]: val }));
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <SectionLabel>Code Paths</SectionLabel>

      <div style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden',
      }}>
        {CODE_PATH_MODES.map((m, i) => {
          const selected = cfg.mode === m.value;
          return (
            <div
              key={m.value}
              onClick={() => set('mode', m.value)}
              style={{
                padding: '14px 16px',
                borderBottom: i < CODE_PATH_MODES.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer',
                background: selected ? 'rgba(var(--accent-rgb, 99,102,241),0.06)' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              {/* Radio row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                  border: `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                  background: selected ? 'var(--accent)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
                    {m.label}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {m.description}
                  </div>

                  {/* Inline controls when selected */}
                  {selected && (
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12 }}
                      onClick={e => e.stopPropagation()}
                    >
                      {m.value === 'separate' && (
                        <>
                          <LabelledInput label="Repo column">
                            <ColSelect value={cfg.repoCol} onChange={v => set('repoCol', v)} headers={headers} />
                          </LabelledInput>
                          <LabelledInput label="Code file column">
                            <ColSelect value={cfg.fileCol} onChange={v => set('fileCol', v)} headers={headers} />
                          </LabelledInput>
                        </>
                      )}
                      {m.value === 'path_prefix' && (
                        <LabelledInput label="Code paths column" hint="e.g. telephony-service/src/**/*.java">
                          <ColSelect value={cfg.fileCol} onChange={v => set('fileCol', v)} headers={headers} />
                        </LabelledInput>
                      )}
                      {m.value === 'shared_repo' && (
                        <>
                          <LabelledInput label="Repository name">
                            <input
                              className="form-input"
                              style={{ margin: 0, maxWidth: 220, fontSize: 13 }}
                              placeholder="e.g. telephony-service"
                              value={cfg.repoFixed}
                              onChange={e => set('repoFixed', e.target.value)}
                            />
                          </LabelledInput>
                          <LabelledInput label="Code file column">
                            <ColSelect value={cfg.fileCol} onChange={v => set('fileCol', v)} headers={headers} />
                          </LabelledInput>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Small layout helpers ──────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
    }}>
      {children}
    </div>
  );
}

function RequiredBadge() {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: 'var(--danger)', marginLeft: 6,
      background: 'rgba(239,68,68,0.1)', borderRadius: 4, padding: '1px 5px',
      letterSpacing: '0.04em',
    }}>
      ALL REQUIRED
    </span>
  );
}

function LabelledInput({ label, hint, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 600 }}>
        {label}
        {hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontWeight: 600,
  color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)',
};

const tdLabelStyle = {
  padding: '10px 16px', color: 'var(--text-primary)', verticalAlign: 'middle',
  width: 180,
};

// ── Step 3 ────────────────────────────────────────────────────────────────────

function Step3({ result, importing, onBackToMapping, onCancel, onViewFeatures, onResolveDuplicates }) {
  if (result.status === 'duplicates') {
    return (
      <DuplicateResult
        result={result}
        importing={importing}
        onBack={onBackToMapping}
        onCancel={onCancel}
        onResolve={onResolveDuplicates}
      />
    );
  }
  if (result.status === 'unresolved_repos') {
    return <UnresolvedReposResult result={result} onBack={onBackToMapping} onCancel={onCancel} />;
  }
  return <SuccessResult result={result} onViewFeatures={onViewFeatures} />;
}

function UnresolvedReposResult({ result, onBack, onCancel }) {
  const repos = result.unresolved_repos || [];
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)',
        borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 20,
        color: '#ef4444',
      }}>
        <IconWarning />
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Import aborted — unresolved repositories</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {result.message}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Repo names not found in Repositories ({repos.length})
        </div>
        <div style={{
          border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden',
        }}>
          {repos.map((r, i) => (
            <div key={r} style={{
              padding: '8px 14px', fontSize: 13, fontFamily: 'monospace',
              borderBottom: i < repos.length - 1 ? '1px solid var(--border)' : 'none',
              color: '#ef4444',
            }}>
              {r}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={onBack}>Back to Column Mapping</button>
      </div>
    </div>
  );
}

function DuplicateResult({ result, importing, onBack, onCancel, onResolve }) {
  const dupes = result.duplicates || [];

  // One sheet row can match several existing features and vice-versa, so count
  // distinct rows and distinct targets rather than raw pairs.
  const dupRowCount = result.duplicate_rows
    ?? new Set(dupes.map(d => d.incoming.row_num)).size;
  const targetCount = new Set(dupes.map(d => d.existing.feature_id)).size;

  function handleReplace() {
    if (!window.confirm(
      `This will overwrite ${targetCount} existing feature${targetCount !== 1 ? 's' : ''} ` +
      `with the values from your spreadsheet, and import the remaining rows as new features.\n\n` +
      `Feature IDs are preserved. This cannot be undone. Continue?`
    )) return;
    onResolve('replace');
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 24,
        color: 'var(--danger, #ef4444)',
      }}>
        <IconWarning />
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Import paused — {dupes.length} potential duplicate{dupes.length !== 1 ? 's' : ''} found
            {' '}across {dupRowCount} row{dupRowCount !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: 13 }}>
            No records have been written yet. Review the matches below, then choose how to
            proceed — or fix the spreadsheet and try again.
          </div>
        </div>
      </div>

      <div style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        overflow: 'auto', marginBottom: 24, maxHeight: 380,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2, #1e293b)', position: 'sticky', top: 0, zIndex: 1 }}>
              {['Row', 'Incoming Major Feature', 'Incoming Sub Feature',
                'Matched Feature ID', 'Matched Major Feature', 'Similarity'].map(h => (
                <th key={h} style={{
                  padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dupes.map((d, i) => (
              <tr key={i} style={{
                borderBottom: i < dupes.length - 1 ? '1px solid var(--border)' : 'none',
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
              }}>
                <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>#{d.incoming.row_num}</td>
                <td style={{ padding: '8px 12px', maxWidth: 180, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.incoming.major_feature}
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--text-secondary)',
                  maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.incoming.sub_feature || '—'}
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--accent)', fontFamily: 'monospace',
                  whiteSpace: 'nowrap' }}>
                  {d.existing.feature_id}
                </td>
                <td style={{ padding: '8px 12px', maxWidth: 180, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.existing.major_feature}
                </td>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                  <span style={{
                    background: d.similarity >= 95 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                    color: d.similarity >= 95 ? 'var(--danger, #ef4444)' : '#f59e0b',
                    padding: '2px 8px', borderRadius: 12, fontWeight: 600, fontSize: 11,
                  }}>
                    {d.similarity}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── How to resolve ── */}
      <div style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        padding: '14px 16px', marginBottom: 20,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Resolve duplicates
        </div>
        <ul style={{
          margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7,
        }}>
          <li>
            <strong style={{ color: 'var(--text-primary)' }}>Replace</strong> — overwrites the
            matched features&#39; major feature, sub feature and description with the spreadsheet
            values, and imports every other row as new. Existing Feature IDs are kept, and code
            paths are only swapped for rows that actually have code paths in the sheet.
          </li>
          <li>
            <strong style={{ color: 'var(--text-primary)' }}>Skip</strong> — leaves the matched
            features untouched and imports only the rows that did not match anything.
          </li>
          <li>
            <strong style={{ color: 'var(--text-primary)' }}>Go back &amp; fix</strong> — writes
            nothing; edit the spreadsheet and re-run the import.
          </li>
        </ul>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={importing}>Cancel</button>
        <button className="btn btn-secondary" onClick={onBack} disabled={importing}>
          <IconArrowLeft /> Go Back &amp; Fix
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => onResolve('skip')}
          disabled={importing}
        >
          Skip duplicates, import the rest
        </button>
        <button
          className="btn btn-primary"
          onClick={handleReplace}
          disabled={importing}
        >
          {importing
            ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Importing…</>
            : `Replace ${targetCount} existing entr${targetCount !== 1 ? 'ies' : 'y'}`}
        </button>
      </div>
    </div>
  );
}

function SuccessResult({ result, onViewFeatures }) {
  const [deleting, setDeleting]     = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [rolledBack, setRolledBack] = useState(false);

  const inserted        = result.inserted ?? 0;
  const replaced        = result.replaced ?? 0;
  const processed       = result.processed ?? inserted;   // features written, new + overwritten
  const dupesSkipped    = result.duplicates_skipped ?? 0;
  const pct             = result.repo_mapping_pct ?? 0;
  const withPaths       = result.features_with_code_paths ?? 0;
  const matchedRepos    = result.features_with_matched_repo ?? 0;
  const unmatched       = withPaths - matchedRepos;       // had a repo name but not found in DB
  const noPathAtAll     = processed - withPaths;          // no repo name at all
  const allMapped       = pct === 100;
  const unresolvedRows  = result.unresolved_rows ?? 0;
  const unresolvedRepos = result.unresolved_repos || [];

  async function handleDeleteAll() {
    const n = inserted;
    if (!window.confirm(
      `This will permanently delete the ${n} NEW feature${n !== 1 ? 's' : ''} imported in this session.\n\n` +
      (replaced > 0
        ? `The ${replaced} existing feature${replaced !== 1 ? 's' : ''} that ${replaced !== 1 ? 'were' : 'was'} overwritten cannot be restored — their previous values are gone.\n\n`
        : '') +
      `This cannot be undone. Continue?`
    )) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await axios.post('/features/bulk-delete', { ids: result.inserted_ids });
      setRolledBack(true);
    } catch (e) {
      setDeleteError(e.response?.data?.error || 'Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  if (rolledBack) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <div style={{
          fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20,
        }}>
          Import rolled back — {inserted} newly imported feature{inserted !== 1 ? 's' : ''} deleted.
          {replaced > 0 && (
            <div style={{ color: '#f59e0b', marginTop: 8, fontSize: 13 }}>
              The {replaced} replaced feature{replaced !== 1 ? 's were' : ' was'} left as imported —
              overwritten values cannot be restored.
            </div>
          )}
        </div>
        <a href="/features" className="btn btn-secondary">Back to Features</a>
      </div>
    );
  }

  // Nothing was written — e.g. every row was skipped as a duplicate.
  if (processed === 0) {
    return (
      <div>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 24,
        }}>
          <div style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}><IconWarning /></div>
          <div>
            <div style={{ fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>
              Nothing was imported
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {dupesSkipped > 0 && (
                <>All {dupesSkipped} valid row{dupesSkipped !== 1 ? 's' : ''} matched an existing
                feature and {dupesSkipped !== 1 ? 'were' : 'was'} skipped. </>
              )}
              {result.skipped > 0 && (
                <>{result.skipped} row{result.skipped !== 1 ? 's' : ''} had blank required fields. </>
              )}
              No records were created or changed.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <a href="/features" className="btn btn-secondary">Back to Features</a>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Success banner ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
        borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 16,
        color: '#22c55e',
      }}>
        <IconCheck />
        <div>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            {inserted} feature{inserted !== 1 ? 's' : ''} imported
            {replaced > 0 && `, ${replaced} existing feature${replaced !== 1 ? 's' : ''} replaced`}
            {' '}successfully
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Product: <strong>{result.product_name}</strong>
            {result.code_files_inserted > 0 && (
              <span> &nbsp;·&nbsp; {result.code_files_inserted} code path{result.code_files_inserted !== 1 ? 's' : ''} linked</span>
            )}
            {dupesSkipped > 0 && (
              <span style={{ color: '#f59e0b' }}> &nbsp;·&nbsp; {dupesSkipped} duplicate row{dupesSkipped !== 1 ? 's' : ''} skipped</span>
            )}
            {result.skipped > 0 && (
              <span style={{ color: '#f59e0b' }}> &nbsp;·&nbsp; {result.skipped} row{result.skipped !== 1 ? 's' : ''} skipped (blank required fields)</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Repository mapping summary ── */}
      <div style={{
        border: `1px solid ${allMapped ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.35)'}`,
        borderRadius: 'var(--radius)', padding: '16px 18px', marginBottom: 16,
        background: allMapped ? 'rgba(34,197,94,0.05)' : 'rgba(245,158,11,0.05)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Repository Mapping
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{
            flex: 1, height: 10, borderRadius: 6,
            background: 'var(--surface-2, #1e293b)', overflow: 'hidden',
          }}>
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: 6,
              background: allMapped ? '#22c55e' : '#f59e0b',
              transition: 'width 0.4s ease',
            }} />
          </div>
          <span style={{
            fontWeight: 700, fontSize: 15,
            color: allMapped ? '#22c55e' : '#f59e0b',
            minWidth: 44, textAlign: 'right',
          }}>
            {pct}%
          </span>
        </div>

        {/* Counts breakdown */}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
          <strong style={{ color: allMapped ? '#22c55e' : 'var(--text-primary)' }}>
            {matchedRepos} of {processed} feature{processed !== 1 ? 's' : ''}
          </strong>{' '}
          matched a known repository.
        </div>
        {withPaths > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
            <span>✓ {matchedRepos} repo{matchedRepos !== 1 ? 's' : ''} found in Repositories</span>
            {unmatched > 0 && (
              <span style={{ color: '#f59e0b' }}>
                ✗ {unmatched} repo name{unmatched !== 1 ? 's' : ''} not found in Repositories
              </span>
            )}
            {noPathAtAll > 0 && (
              <span style={{ color: 'var(--text-tertiary, #64748b)' }}>
                — {noPathAtAll} feature{noPathAtAll !== 1 ? 's' : ''} had no repo name
              </span>
            )}
          </div>
        )}

        {/* Advisory when not 100% */}
        {!allMapped && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 6, padding: '10px 12px',
          }}>
            <div style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}><IconWarning /></div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <strong style={{ color: '#f59e0b' }}>100% of features should be matched to a known repository.</strong>
              <br />
              {unmatched > 0 && (
                <span>
                  {unmatched} feature{unmatched !== 1 ? 's have' : ' has'} a repo name that was not found in Repositories —
                  check spelling or add the repository first.{' '}
                </span>
              )}
              {noPathAtAll > 0 && (
                <span>
                  {noPathAtAll} feature{noPathAtAll !== 1 ? 's have' : ' has'} no code paths at all.{' '}
                </span>
              )}
              Go back and re-import with code path columns configured, or add them manually
              via each feature's detail page.
            </div>
          </div>
        )}
      </div>

      {/* ── Spreadsheet remarks for unresolved repos ── */}
      {unresolvedRows > 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}><IconWarning /></div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: '#f59e0b' }}>
              Import completed with unresolved repositories
            </strong>
            <br />
            {unresolvedRows} spreadsheet row{unresolvedRows !== 1 ? 's' : ''} had code paths whose
            repository names were not found. A short remark was added in the last sheet column for
            those rows.
            {unresolvedRepos.length > 0 && (
              <span>
                {' '}Missing repo name{unresolvedRepos.length !== 1 ? 's' : ''}:{' '}
                <span style={{ fontFamily: 'monospace' }}>{unresolvedRepos.join(', ')}</span>.
              </span>
            )}
            {result.sheet_remarks_error && (
              <div style={{ color: '#ef4444', marginTop: 6 }}>
                Could not write remarks to the sheet: {result.sheet_remarks_error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Post-import governance warning ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)',
        borderRadius: 'var(--radius)', padding: '16px 18px', marginBottom: 24,
      }}>
        <div style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}><IconWarning /></div>
        <div>
          <div style={{ fontWeight: 700, color: '#f59e0b', marginBottom: 6, fontSize: 14 }}>
            Important: Manage features here from now on
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Your features are now managed in this application. Going forward,{' '}
            <strong style={{ color: 'var(--text-primary)' }}>
              please add, edit, and delete features directly in this tool
            </strong>{' '}
            rather than in the spreadsheet. Changes made to the spreadsheet will{' '}
            <strong style={{ color: 'var(--text-primary)' }}>not</strong> be automatically
            reflected here, and re-importing may introduce duplicates.
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      {deleteError && (
        <div className="error-msg" style={{ marginBottom: 12 }}>{deleteError}</div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
        {inserted > 0 ? (
          <button
            className="btn btn-secondary"
            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={handleDeleteAll}
            disabled={deleting}
            title={replaced > 0
              ? 'Only the newly imported records are deleted — replaced features keep their new values'
              : undefined}
          >
            {deleting
              ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Deleting…</>
              : `Delete ${inserted} newly imported record${inserted !== 1 ? 's' : ''}`}
          </button>
        ) : <span />}
        <button className="btn btn-primary" onClick={onViewFeatures}>
          View Imported Features
        </button>
      </div>
    </div>
  );
}
